import { useCallback, useEffect, useState } from 'react'
import { ChevronDown, ChevronRight, FileText, FolderClosed, X } from 'lucide-react'
import { api, type ApiError } from '../lib/api'
import { cn } from '../lib/cn'
import { Button } from './ui/button'
import { StatusBox } from './ui/badge'
import { EmptyState } from './ui/empty-state'

type FileEntry = { path: string; size: number }
type TreeNode = { name: string; path: string; children: TreeNode[]; size?: number }

type Props = {
  projectId: string
  accessToken: string
  refreshKey: number
}

export function FileTree({ projectId, accessToken, refreshKey }: Props) {
  const [files, setFiles] = useState<FileEntry[] | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [content, setContent] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .listFiles(accessToken, projectId)
      .then((res) => setFiles(res.files))
      .catch((err: ApiError) => setError(err.title ?? 'Failed to load files'))
  }, [accessToken, projectId, refreshKey])

  const onSelect = useCallback(
    (path: string) => {
      if (selected === path) {
        setSelected(null)
        setContent(null)
        return
      }
      setSelected(path)
      setContent(null)
      setLoading(true)
      api
        .readFile(accessToken, projectId, path)
        .then((res) => setContent(res.content))
        .catch(() => setContent('(failed to load file)'))
        .finally(() => setLoading(false))
    },
    [accessToken, projectId, selected],
  )

  if (error) {
    return (
      <div className="p-4">
        <StatusBox tone="destructive">{error}</StatusBox>
      </div>
    )
  }
  if (!files) {
    return <p className="p-5 text-sm text-muted-foreground">Loading files…</p>
  }
  if (files.length === 0) {
    return (
      <div className="p-4">
        <EmptyState
          icon={<FolderClosed />}
          title="No files yet"
          body="Send a prompt in the Chat tab and the agent generates the first version of your app here."
        />
      </div>
    )
  }

  const tree = buildTree(files)

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Tree */}
      <div className="min-h-0 flex-1 overflow-y-auto border-b p-2 text-sm">
        {tree.map((node) => (
          <TreeItem key={node.path} node={node} selected={selected} onSelect={onSelect} depth={0} />
        ))}
      </div>

      {/* File content */}
      {selected && (
        <div className="flex min-h-0 flex-1 flex-col bg-muted/30 p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="truncate font-mono text-xs text-muted-foreground">{selected}</p>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Close file"
              onClick={() => { setSelected(null); setContent(null) }}
            >
              <X />
            </Button>
          </div>
          {loading ? (
            <p className="font-mono text-xs text-muted-foreground">Loading…</p>
          ) : (
            <pre className="min-h-0 flex-1 overflow-auto rounded-xl bg-muted p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap text-foreground">
              {content}
            </pre>
          )}
        </div>
      )}
    </div>
  )
}

function TreeItem({
  node,
  selected,
  onSelect,
  depth,
}: {
  node: TreeNode
  selected: string | null
  onSelect: (path: string) => void
  depth: number
}) {
  const [open, setOpen] = useState(depth < 2)
  const isDir = node.children.length > 0
  const isSelected = selected === node.path

  if (isDir) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex w-full items-center gap-1.5 rounded-lg px-2 py-1 text-left text-muted-foreground transition-colors hover:bg-muted hover:text-foreground [&_svg]:size-3.5 [&_svg]:shrink-0"
          style={{ paddingLeft: depth * 12 + 8 }}
        >
          {open ? <ChevronDown /> : <ChevronRight />}
          <span className="font-medium">{node.name}/</span>
        </button>
        {open &&
          node.children.map((child) => (
            <TreeItem key={child.path} node={child} selected={selected} onSelect={onSelect} depth={depth + 1} />
          ))}
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={() => onSelect(node.path)}
      className={cn(
        'flex w-full items-center gap-1.5 rounded-lg px-2 py-1 text-left transition-colors [&_svg]:size-3.5 [&_svg]:shrink-0',
        isSelected ? 'bg-volt-soft text-volt-ink' : 'text-foreground hover:bg-muted',
      )}
      style={{ paddingLeft: depth * 12 + 20 }}
    >
      <FileText className={isSelected ? 'text-volt-ink' : 'text-muted-foreground'} />
      <span className="truncate">{node.name}</span>
      {node.size != null && (
        <span className={cn('ml-auto text-[10px] tabular-nums', isSelected ? 'text-volt-ink/70' : 'text-muted-foreground')}>
          {node.size < 1024 ? `${node.size}B` : `${(node.size / 1024).toFixed(1)}K`}
        </span>
      )}
    </button>
  )
}

function buildTree(files: FileEntry[]): TreeNode[] {
  const root: TreeNode[] = []

  for (const file of files) {
    const parts = file.path.split('/')
    let current = root
    for (let i = 0; i < parts.length; i++) {
      const name = parts[i]!
      const isLast = i === parts.length - 1
      const existing = current.find((n) => n.name === name)
      if (existing) {
        current = existing.children
      } else {
        const node: TreeNode = {
          name,
          path: parts.slice(0, i + 1).join('/'),
          children: [],
          ...(isLast ? { size: file.size } : {}),
        }
        current.push(node)
        current = node.children
      }
    }
  }

  // Sort: directories first, then files, alphabetical within each.
  const sortTree = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => {
      const aDir = a.children.length > 0 ? 0 : 1
      const bDir = b.children.length > 0 ? 0 : 1
      if (aDir !== bDir) return aDir - bDir
      return a.name.localeCompare(b.name)
    })
    for (const node of nodes) sortTree(node.children)
  }
  sortTree(root)

  return root
}
